import { useState, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { API_ENDPOINTS } from "@/lib/constants";
import type { HitResult, ActionType } from "@/lib/types";

interface UseSearchReturn {
  // State
  question: string;
  answerSize: number;
  isLoading: boolean;
  loadingType: ActionType | null;
  answer: string | undefined;
  hits: HitResult[];
  cost: number | undefined;
  lastActionType: ActionType | null;
  hasQueried: boolean;
  hasResults: boolean;

  // Actions
  setQuestion: (q: string) => void;
  setAnswerSize: (size: number) => void;
  handleAction: (type: ActionType) => Promise<void>;
  reset: () => void;
}

export function useSearch(): UseSearchReturn {
  const [question, setQuestion] = useState("");
  const [answerSize, setAnswerSize] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingType, setLoadingType] = useState<ActionType | null>(null);
  const [answer, setAnswer] = useState<string | undefined>();
  const [hits, setHits] = useState<HitResult[]>([]);
  const [cost, setCost] = useState<number | undefined>();
  const [lastActionType, setLastActionType] = useState<ActionType | null>(null);
  const [hasQueried, setHasQueried] = useState(false);

  const hasResults = Boolean(answer || hits.length > 0);

  const reset = useCallback(() => {
    setHasQueried(false);
    setQuestion("");
    setAnswer(undefined);
    setHits([]);
    setCost(undefined);
    setLastActionType(null);
  }, []);

  const handleAction = useCallback(
    async (type: ActionType) => {
      if (!question.trim()) {
        toast({
          title: "Question requise",
          description: "Veuillez entrer une question ou un mot-clé.",
          variant: "destructive",
        });
        return;
      }

      setIsLoading(true);
      setLoadingType(type);
      setAnswer(undefined);
      setHits([]);
      setCost(undefined);
      setHasQueried(true);

      try {
        const response = await fetch(API_ENDPOINTS[type], {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question,
            answerSize: String(answerSize),
          }),
        });

        if (!response.ok) {
          if (response.status === 429) {
            throw new Error("RATE_LIMIT");
          } else if (response.status === 403) {
            throw new Error("FORBIDDEN");
          } else if (response.status >= 500) {
            throw new Error("SERVER_ERROR");
          } else {
            throw new Error("HTTP_ERROR");
          }
        }

        setLastActionType(type);

        if (type === "analyze") {
          // SSE Streaming for analyze
          const reader = response.body?.getReader();
          if (!reader) throw new Error("NO_READER");

          const decoder = new TextDecoder();
          let fullText = "";
          let streamCost = 0;
          let streamResources: HitResult[] = [];

          let streamDone = false;

          while (!streamDone) {
            const { done, value } = await reader.read();
            if (done) break;
            const text = decoder.decode(value);
            const lines = text.split("\n");

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.type === "token") {
                    fullText += data.data;
                    setAnswer(fullText);
                  } else if (data.type === "resources") {
                    streamResources = data.resources || [];
                    setHits(streamResources);
                  } else if (data.type === "cost") {
                    streamCost = data.cost;
                    setCost(streamCost);
                  } else if (data.type === "done") {
                    streamDone = true;
                    break;
                  }
                } catch (parseError) {
                  console.warn("SSE parse error:", parseError);
                }
              }
            }
          }

          reader.releaseLock();
        } else {
          // Simple fetch for find
          const json = await response.json();
          setHits(json.resources || []);
        }
      } catch (error) {
        console.error("[DEBUG] Error:", error);

        let errorMessage = "Une erreur est survenue lors de la requête.";

        if (error instanceof Error) {
          if (error.message === "RATE_LIMIT") {
            errorMessage = "Limite de requêtes atteinte. Veuillez patienter.";
          } else if (error.message === "FORBIDDEN") {
            errorMessage = "Accès refusé à l'API.";
          } else if (error.message === "SERVER_ERROR") {
            errorMessage = "Le serveur rencontre des difficultés.";
          }
        }

        toast({
          title: "Erreur",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        setLoadingType(null);
      }
    },
    [question, answerSize],
  );

  return {
    question,
    answerSize,
    isLoading,
    loadingType,
    answer,
    hits,
    cost,
    lastActionType,
    hasQueried,
    hasResults,
    setQuestion,
    setAnswerSize,
    handleAction,
    reset,
  };
}
